import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { Crop, getCrops } from '@next/arable-lib/crop';
import { SelectItem } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import { getMostUsedCrops } from 'src/app/registration/store/selectors/most-used-crops.selectors';
import { SiteState } from 'src/app/shared/models';
import { getAvailableCropYears, getCropYear } from 'src/app/store/selectors';

import { NextSalesPotentialDto } from '../registration/models';
import {
  DeleteSalesPotentialRequest,
  LoadSalesPotentialsRequest,
  UpsertSalesPotentialRequestAction,
} from './actions/sales-potential.actions';
import { SalesPotentialDialogComponent } from './components/sales-potential-dialog/sales-potential-dialog.component';
import {
  selectSalesPotentialLoading,
  selectSalesPotentials,
} from './selectors/sales-potential.selectors';

interface SalesPotentialRow {
  id: number;
  crop: string;
  hectare: number;
  gbm: boolean;
  bemesting: boolean;
  zaaigoed: boolean;
  isSkal: boolean;
}

enum SelectAction {
  year = 'year',
}

@Component({
  selector: 'app-sales-potential',
  templateUrl: './sales-potential.component.html',
  styleUrls: ['./sales-potential.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SalesPotentialComponent implements OnInit {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  displayedColumns: string[] = [
    'crop',
    'hectare',
    'gbm',
    'fertilizer',
    'seed',
    'isSkal',
    'actions',
  ];
  salesPotentialData$: Observable<SalesPotentialRow[]>;
  loading$: Observable<boolean>;
  selectItems$: Observable<SelectItem<unknown>[]>;
  selectedYear: number;

  ngOnInit(): void {
    this.selectItems$ = this.store.pipe(
      select(getAvailableCropYears),
      withLatestFrom(this.store.pipe(select(getCropYear))),
      map(([availableYears, year]) => {
        const resolvedYear =
          typeof year === 'number' ? year : (availableYears?.[0] ?? new Date().getFullYear());
        this.selectedYear = resolvedYear;
        return [
          {
            icon: 'calendar_today',
            viewValue: 'Year',
            key: SelectAction.year,
            value: resolvedYear,
            items: (availableYears || []).map((availableYear) => ({
              value: availableYear,
              viewValue: availableYear.toString(),
            })),
          },
        ];
      }),
    );

    this.store
      .pipe(
        select(getCropYear),
        filter((year: number): year is number => typeof year === 'number'),
        take(1),
      )
      .subscribe((year) => this.store.dispatch(new LoadSalesPotentialsRequest(year)));

    this.salesPotentialData$ = this.store.pipe(select(selectSalesPotentials)).pipe(
      withLatestFrom(this.store.pipe(select(getCrops))),
      map(([salesPotentials, crops]) =>
        salesPotentials
          .map((dto) => this.mapDtoToRow(dto, crops))
          .sort((a, b) => a.crop.localeCompare(b.crop)),
      ),
    );

    this.loading$ = this.store.select(selectSalesPotentialLoading);
  }

  selectAction([action, value]: [string, unknown]) {
    if (action === SelectAction.year) {
      this.selectedYear = value as number;
      this.store.dispatch(new LoadSalesPotentialsRequest(this.selectedYear));
    }
  }

  private mapDtoToRow(dto: NextSalesPotentialDto, crops: Crop[]): SalesPotentialRow {
    const cropName = crops.find((c) => c.id === dto.cropId)?.name || '';

    return {
      id: dto.id,
      crop: cropName,
      hectare: dto.areaInHectares,
      gbm: dto.salesPotentialGbm,
      bemesting: dto.salesPotentialBem,
      zaaigoed: dto.salesPotentialSeed,
      isSkal: dto.isSkal,
    };
  }

  onAddCrop(): void {
    this.store
      .pipe(select(getCrops), withLatestFrom(this.store.pipe(select(getMostUsedCrops))), take(1))
      .subscribe(([crops, mostUsedCrops]) => {
        this.dialog
          .form(SalesPotentialDialogComponent, {
            year: this.selectedYear,
            crops,
            mostUsedCrops,
            pushHistoryState: false,
            title: this.locale.translate('salesPotential.addSalesPotential'),
          })
          .pipe(take(1))
          .subscribe(({ status, result }) => {
            if (status === DIALOG_RETURN_TYPES.confirm) {
              this.store.dispatch(new UpsertSalesPotentialRequestAction(result));
            }
          });
      });
  }

  onEditRow(row: SalesPotentialRow): void {
    this.store
      .pipe(
        select(selectSalesPotentials),
        withLatestFrom(
          this.store.pipe(select(getCrops)),
          this.store.pipe(select(getMostUsedCrops)),
        ),
        map(([potentials, crops, mostUsedCrops]) => ({
          salesPotential: potentials.find((p) => p.id === row.id),
          crops,
          mostUsedCrops,
        })),
        filter(
          (
            data,
          ): data is {
            salesPotential: NextSalesPotentialDto;
            crops: Crop[];
            mostUsedCrops: Crop[];
          } => !!data.salesPotential,
        ),
        take(1),
      )
      .subscribe(({ salesPotential, crops, mostUsedCrops }) => {
        this.dialog
          .form(SalesPotentialDialogComponent, {
            year: this.selectedYear,
            crops: crops,
            mostUsedCrops: mostUsedCrops,
            salesPotential: salesPotential,
            pushHistoryState: false,
            title: this.locale.translate('salesPotential.editSalesPotential'),
          })
          .pipe(take(1))
          .subscribe(({ status, result }) => {
            if (status === DIALOG_RETURN_TYPES.confirm) {
              this.store.dispatch(new UpsertSalesPotentialRequestAction(result));
            }
          });
      });
  }

  onDeleteRow(row: SalesPotentialRow): void {
    this.dialog
      .confirm({
        title: this.locale.translate('salesPotential.remove'),
        text: this.locale.translate('salesPotential.removeSalesPotential'),
      })
      .pipe(take(1))
      .subscribe(({ status }) => {
        if (status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(new DeleteSalesPotentialRequest(row.id));
        }
      });
  }

  trackByFn(index: number): number {
    return index;
  }
}
